/**
 * QR Code Utilities
 * Funciones para generar códigos QR para direcciones de wallet
 */

// Tipos para QR Code
export interface QRCodeOptions {
  size?: number;
  margin?: number;
  colorDark?: string;
  colorLight?: string;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

export interface QRCodeResult {
  success: boolean;
  dataURL?: string;
  error?: string;
}

/**
 * Cache para QR codes generados
 */
const qrCache = new Map<string, string>();

/**
 * Genera un código QR para una dirección de wallet
 * @param address - Dirección de la wallet
 * @param canvas - Elemento canvas donde renderizar el QR
 * @param options - Opciones de configuración del QR
 * @returns Promise<void>
 */
export async function generateQRCode(
  address: string,
  canvas: HTMLCanvasElement,
  options: QRCodeOptions = {}
): Promise<void> {
  try {
    if (!address || !canvas) {
      throw new Error('Address and canvas are required');
    }

    // Configuración por defecto
    const config = {
      size: options.size || 256,
      margin: options.margin || 4,
      colorDark: options.colorDark || '#000000',
      colorLight: options.colorLight || '#ffffff',
      errorCorrectionLevel: options.errorCorrectionLevel || 'H' as const,
    };

    console.log('[QR Utils] Generando QR code para:', address);

    // Verificar si ya está en cache
    const cacheKey = `${address}_${JSON.stringify(config)}`;
    if (qrCache.has(cacheKey)) {
      const cachedDataURL = qrCache.get(cacheKey)!;
      await renderDataURLToCanvas(cachedDataURL, canvas);
      console.log('[QR Utils] QR code cargado desde cache');
      return;
    }

    // Cargar biblioteca QR Code dinámicamente
    const QRCode = await loadQRCodeLibrary();

    // Configurar canvas
    canvas.width = config.size;
    canvas.height = config.size;

    // qrcodejs espera un contenedor, no un canvas. Si le pasamos un canvas,
    // agregará un canvas hijo, lo cual no es válido y no se dibuja.
    const tempContainer = document.createElement('div');

    // Crear instancia de QR Code
    const qrCode = new QRCode(tempContainer, {
      text: address,
      width: config.size,
      height: config.size,
      colorDark: config.colorDark,
      colorLight: config.colorLight,
      correctLevel: QRCode.CorrectLevel[config.errorCorrectionLevel],
    });

    // Generar el código QR
    qrCode.makeCode(address);

    // Extraer el canvas generado por la librería
    const generatedCanvas = tempContainer.querySelector('canvas');
    if (generatedCanvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(generatedCanvas, 0, 0, canvas.width, canvas.height);
      }
    } else {
      console.warn('[QR Utils] No se encontró el canvas generado por la librería QR');
    }

    // Guardar en cache como data URL
    const dataURL = canvas.toDataURL('image/png');
    qrCache.set(cacheKey, dataURL);

    console.log('[QR Utils] QR code generado exitosamente');
  } catch (error) {
    console.error('[QR Utils] Error generando QR code:', error);
    
    // Renderizar mensaje de error en el canvas
    renderErrorToCanvas(canvas, 'Error generando QR', address);
    throw error;
  }
}

/**
 * Genera un código QR y retorna como Data URL
 * @param address - Dirección de la wallet
 * @param options - Opciones de configuración del QR
 * @returns Promise<QRCodeResult>
 */
export async function generateQRDataURL(
  address: string,
  options: QRCodeOptions = {}
): Promise<QRCodeResult> {
  try {
    if (!address) {
      return { success: false, error: 'Address is required' };
    }

    // Configuración por defecto
    const config = {
      size: options.size || 256,
      margin: options.margin || 4,
      colorDark: options.colorDark || '#000000',
      colorLight: options.colorLight || '#ffffff',
      errorCorrectionLevel: options.errorCorrectionLevel || 'H' as const,
    };

    // Verificar cache
    const cacheKey = `${address}_${JSON.stringify(config)}`;
    if (qrCache.has(cacheKey)) {
      return { 
        success: true, 
        dataURL: qrCache.get(cacheKey)! 
      };
    }

    // Crear canvas temporal
    console.log('[QR Utils] Creando canvas temporal para QR Data URL');
    const canvas = document.createElement('canvas');
    canvas.width = config.size;
    canvas.height = config.size;

    // Generar QR
    console.log(`[QR Utils] Generando QR para ${address} en canvas temporal`);
    await generateQRCode(address, canvas, options);

    // Obtener data URL
    const dataURL = canvas.toDataURL('image/png');
    console.log('[QR Utils] Data URL generado exitosamente (longitud:', dataURL.length, ')');

    return { success: true, dataURL };
  } catch (error) {
    console.error('[QR Utils] Error generando QR data URL:', error);
    return { 
      success: false, 
      error: (error as Error).message 
    };
  }
}

/**
 * Limpia el cache de QR codes
 */
export function clearQRCache(): void {
  qrCache.clear();
  console.log('[QR Utils] Cache de QR codes limpiado');
}

/**
 * Obtiene el tamaño actual del cache
 */
export function getQRCacheSize(): number {
  return qrCache.size;
}

/**
 * Carga la biblioteca QR Code dinámicamente
 * @returns Promise<any> - Biblioteca QRCode
 */
async function loadQRCodeLibrary(): Promise<any> {
  // Si ya está cargada globalmente, usarla
  if ((window as any).QRCode) {
    return (window as any).QRCode;
  }

  console.log('[QR Utils] Cargando biblioteca QR Code dinámicamente...');
  return new Promise((resolve, reject) => {
    // Intentar cargar qrcodejs
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js';
    
    script.onload = () => {
      console.log('[QR Utils] Biblioteca qrcodejs cargada exitosamente');
      if ((window as any).QRCode) {
        resolve((window as any).QRCode);
      } else {
        // Fallback: cargar qrcode-generator
        loadQRCodeGeneratorFallback()
          .then(resolve)
          .catch(reject);
      }
    };

    script.onerror = () => {
      // Fallback: cargar qrcode-generator
      loadQRCodeGeneratorFallback()
        .then(resolve)
        .catch(reject);
    };

    document.head.appendChild(script);
  });
}

/**
 * Carga qrcode-generator como fallback
 */
async function loadQRCodeGeneratorFallback(): Promise<any> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
    
    script.onload = () => {
      if ((window as any).qrcode) {
        // Crear wrapper compatible con QRCode.js
        const QRCodeWrapper = createQRCodeWrapper();
        (window as any).QRCode = QRCodeWrapper;
        resolve(QRCodeWrapper);
      } else {
        reject(new Error('Failed to load QR code library'));
      }
    };

    script.onerror = () => {
      // Último fallback: implementación básica
      const FallbackQRCode = createFallbackQRCode();
      (window as any).QRCode = FallbackQRCode;
      resolve(FallbackQRCode);
    };

    document.head.appendChild(script);
  });
}

/**
 * Crea un wrapper para qrcode-generator compatible con QRCode.js
 */
function createQRCodeWrapper(): any {
  return class QRCodeWrapper {
    private container: HTMLElement;
    private canvas: HTMLCanvasElement;
    private options: any;

    static CorrectLevel = {
      L: 'L',
      M: 'M',
      Q: 'Q',
      H: 'H'
    };

    constructor(container: HTMLElement, options: any) {
      this.container = container;
      this.options = options || {};
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.options.width || 256;
      this.canvas.height = this.options.height || 256;
      this.container.appendChild(this.canvas);
    }

    makeCode(text: string): void {
      try {
        const qr = (window as any).qrcode(4, this.options.correctLevel || 'H');
        qr.addData(text);
        qr.make();

        // Renderizar en canvas
        const moduleCount = qr.getModuleCount();
        const cellSize = Math.floor(this.canvas.width / moduleCount);

        const ctx = this.canvas.getContext('2d');
        if (!ctx) return;

        // Limpiar canvas
        ctx.fillStyle = this.options.colorLight || '#ffffff';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Dibujar QR
        ctx.fillStyle = this.options.colorDark || '#000000';
        for (let row = 0; row < moduleCount; row++) {
          for (let col = 0; col < moduleCount; col++) {
            if (qr.isDark(row, col)) {
              ctx.fillRect(
                col * cellSize,
                row * cellSize,
                cellSize,
                cellSize
              );
            }
          }
        }
      } catch (error) {
        console.error('Error in QRCodeWrapper:', error);
        this.renderError(text);
      }
    }

    private renderError(text: string): void {
      const ctx = this.canvas.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('QR Error', this.canvas.width / 2, this.canvas.height / 2 - 10);
      ctx.font = '10px Arial';
      ctx.fillText(
        text.substring(0, 10) + '...',
        this.canvas.width / 2,
        this.canvas.height / 2 + 10
      );
    }
  };
}

/**
 * Crea una implementación fallback básica
 */
function createFallbackQRCode(): any {
  return class FallbackQRCode {
    private container: HTMLElement;
    private canvas: HTMLCanvasElement;

    static CorrectLevel = {
      L: 'L',
      M: 'M', 
      Q: 'Q',
      H: 'H'
    };

    constructor(container: HTMLElement, options: any) {
      this.container = container;
      this.canvas = document.createElement('canvas');
      this.canvas.width = options?.width || 256;
      this.canvas.height = options?.height || 256;
      this.container.appendChild(this.canvas);
    }

    makeCode(text: string): void {
      const ctx = this.canvas.getContext('2d');
      if (!ctx) return;

      // Mostrar mensaje de fallback
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.fillStyle = '#333333';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('QR Code', this.canvas.width / 2, this.canvas.height / 2 - 20);
      ctx.font = '12px Arial';
      ctx.fillText('Not Available', this.canvas.width / 2, this.canvas.height / 2);
      ctx.font = '10px monospace';
      ctx.fillText(
        text.substring(0, 20) + '...',
        this.canvas.width / 2,
        this.canvas.height / 2 + 20
      );
    }
  };
}

/**
 * Renderiza un data URL en un canvas
 */
async function renderDataURLToCanvas(dataURL: string, canvas: HTMLCanvasElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Cannot get canvas context'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      resolve();
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataURL;
  });
}

/**
 * Renderiza un mensaje de error en el canvas
 */
function renderErrorToCanvas(canvas: HTMLCanvasElement, title: string, subtitle: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Fondo blanco
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Borde rojo
  ctx.strokeStyle = '#ff0000';
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

  // Texto de error
  ctx.fillStyle = '#ff0000';
  ctx.font = '16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(title, canvas.width / 2, canvas.height / 2 - 10);

  ctx.font = '10px monospace';
  ctx.fillStyle = '#666666';
  ctx.fillText(
    subtitle.substring(0, 15) + '...',
    canvas.width / 2,
    canvas.height / 2 + 15
  );
}

export default {
  generateQRCode,
  generateQRDataURL,
  clearQRCache,
  getQRCacheSize,
};